/**
 * AC Compare Page
 * Compare Affiliation Cloud data with local adwstats data to find discrepancies
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  GitCompareArrows,
  Cloud,
  Database,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Filter,
  Info,
  Key,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// AC token helpers (shared pattern with Import page)
// ---------------------------------------------------------------------------
const AC_TOKEN_KEY = 'affiliation-cloud-token';
const AC_TOKEN_EXPIRY_HOURS = 12;

interface StoredToken {
  token: string;
  expiresAt: number;
}

const getStoredACToken = (): string | null => {
  try {
    const stored = localStorage.getItem(AC_TOKEN_KEY);
    if (stored) {
      const parsed: StoredToken = JSON.parse(stored);
      if (parsed.expiresAt > Date.now()) return parsed.token;
      localStorage.removeItem(AC_TOKEN_KEY);
    }
  } catch {
    localStorage.removeItem(AC_TOKEN_KEY);
  }
  return null;
};

const storeACToken = (token: string): void => {
  const expiresAt = Date.now() + AC_TOKEN_EXPIRY_HOURS * 60 * 60 * 1000;
  localStorage.setItem(AC_TOKEN_KEY, JSON.stringify({ token, expiresAt }));
};

const getAdwstatsToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch { /* ignore */ }
  return null;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ComparisonRow {
  name: string;
  ac_id?: number;
  local_account_id?: string;
  match_status: 'matched' | 'ac_only' | 'local_only';
  platform_name?: string;
  platform_provider?: string;
  last_sync_at?: string | null;
  last_sync_status?: string;
  ac: {
    total_commission: number;
    [key: string]: any;
  } | null;
  local: {
    commissions_amount: number;
    [key: string]: any;
  } | null;
  discrepancies: {
    field: string;
    ac_value: number;
    local_value: number;
    diff: number;
    diff_pct: number;
  }[];
}

interface ACAccount {
  id: number;
  name: string;
  advertiser_name: string;
  affiliate_system_name: string;
  status: string;
  brands: string[];
}

interface CompareData {
  period: string;
  publisher_id: number;
  date_range: { from: string; to: string };
  summary: {
    total_ac_accounts: number;
    total_local_accounts: number;
    matched: number;
    ac_only: number;
    local_only: number;
    with_discrepancies: number;
    match_strategies?: Record<string, number>;
  };
  totals: {
    ac: { total_commission: number };
    local: { commissions_amount: number };
    diff: { commission: number };
  };
  comparison: ComparisonRow[];
  ac_accounts: ACAccount[];
  debug: { ac_report_endpoint: string; ac_report_items_count: number; ac_report_raw_preview: string | null };
}

// ---------------------------------------------------------------------------
// Helper: format currency
// ---------------------------------------------------------------------------
const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtNum = (v: number) => v.toLocaleString();

const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ACCompare: React.FC = () => {
  // State
  const [acToken, setAcToken] = useState<string | null>(getStoredACToken());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showTokenPaste, setShowTokenPaste] = useState(false);
  const [pastedToken, setPastedToken] = useState('');

  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [compareData, setCompareData] = useState<CompareData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<'all' | 'matched' | 'ac_only' | 'local_only' | 'discrepancies'>('all');
  const [showDebug, setShowDebug] = useState(false);
  const [resyncingAccounts, setResyncingAccounts] = useState<Set<string>>(new Set());

  // Auto-fetch when token is available
  const isLoggedIn = !!acToken;

  // -------------------------------------------------------------------------
  // AC Login
  // -------------------------------------------------------------------------
  const handleLogin = useCallback(async () => {
    if (!email || !password) {
      toast.error('Enter email and password');
      return;
    }
    setIsLoggingIn(true);
    try {
      const resp = await fetch('/api/import/affiliation-cloud/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAdwstatsToken()}` },
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json() as any;
      if (!data.success) throw new Error(data.error || 'Login failed');
      storeACToken(data.access_token);
      setAcToken(data.access_token);
      toast.success('Connected to Affiliation Cloud');
    } catch (err: any) {
      toast.error(err.message || 'Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  }, [email, password]);

  const handleTokenPaste = useCallback(() => {
    const trimmed = pastedToken.trim();
    if (!trimmed) {
      toast.error('Paste a valid token');
      return;
    }
    storeACToken(trimmed);
    setAcToken(trimmed);
    setPastedToken('');
    toast.success('Token saved - fetching comparison...');
  }, [pastedToken]);

  const handleLogout = () => {
    localStorage.removeItem(AC_TOKEN_KEY);
    setAcToken(null);
    setCompareData(null);
  };

  // -------------------------------------------------------------------------
  // Fetch comparison
  // -------------------------------------------------------------------------
  const fetchComparison = useCallback(async () => {
    if (!acToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const adwToken = getAdwstatsToken();
      const resp = await fetch(`/api/ac-compare?period=${period}&mode=full`, {
        headers: {
          'X-Affiliation-Cloud-Token': acToken,
          ...(adwToken ? { Authorization: `Bearer ${adwToken}` } : {}),
        },
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.message || json.error || 'Comparison failed');
      setCompareData(json.data);
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message || 'Failed to fetch comparison');
    } finally {
      setIsLoading(false);
    }
  }, [acToken, period]);

  // Auto-fetch on login or period change
  useEffect(() => {
    if (isLoggedIn) {
      fetchComparison();
    }
  }, [isLoggedIn, period, fetchComparison]);

  // -------------------------------------------------------------------------
  // Force Resync handler (mirrors Networks page pattern)
  // -------------------------------------------------------------------------
  const handleForceResync = useCallback(async (accountId: string, accountName: string) => {
    const adwToken = getAdwstatsToken();
    if (!adwToken) {
      toast.error('Not authenticated — please log into Adwstats first');
      return;
    }

    setResyncingAccounts(prev => new Set(prev).add(accountId));
    toast.info(`Starting force resync for ${accountName}... This may take several minutes.`);

    let currentOffset = 0;
    let totalSynced = 0;
    let totalRecords = 0;
    let iterations = 0;
    const MAX_ITERATIONS = 20;

    try {
      while (iterations < MAX_ITERATIONS) {
        iterations++;
        const params = new URLSearchParams();
        params.append('force', 'true');
        params.append('skip_players', 'true');
        if (currentOffset > 0) params.append('offset', currentOffset.toString());

        const resp = await fetch(`/api/networks/${accountId}/historical-sync?${params.toString()}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${adwToken}` },
        });
        const json = await resp.json() as any;

        if (!json.success) {
          toast.error(`Resync failed for ${accountName}: ${json.error || 'Unknown error'}`);
          break;
        }

        totalSynced += json.summary?.months_synced_now || 0;
        totalRecords += json.summary?.total_records_inserted || 0;

        if (!json.summary?.has_more_months) {
          toast.success(`Resync complete for ${accountName}: ${totalSynced} months, ${totalRecords} records updated`);
          break;
        }

        currentOffset = json.summary.next_offset || (currentOffset + 6);
      }

      if (iterations >= MAX_ITERATIONS) {
        toast.warning(`Resync for ${accountName} hit iteration limit — ${totalSynced} months synced so far`);
      }
    } catch (err: any) {
      toast.error(`Resync error for ${accountName}: ${err.message}`);
    } finally {
      setResyncingAccounts(prev => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  }, []);

  // -------------------------------------------------------------------------
  // Filter rows
  // -------------------------------------------------------------------------
  const filteredRows = compareData?.comparison?.filter(row => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'discrepancies') return row.discrepancies.length > 0;
    return row.match_status === filterStatus;
  }) || [];


  // -------------------------------------------------------------------------
  // Period selector helpers
  // -------------------------------------------------------------------------
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const mar2023ToNow = `2023-03_${currentMonth}`;

  const months: string[] = [];
  for (let i = 0; i < 36; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  const periodLabel = (p: string) => {
    if (p === 'all') return 'All Time';
    if (p === mar2023ToNow) return 'Mar 2023 → Today';
    if (p.includes('_')) {
      const [from, to] = p.split('_');
      const fmtRange = (s: string) => new Date(s + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      return `${fmtRange(from)} → ${fmtRange(to)}`;
    }
    return new Date(p + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <GitCompareArrows className="h-7 w-7 text-amber-400" />
              AC Compare
            </h1>
            <p className="text-zinc-400 mt-1 text-sm sm:text-base">
              Compare Affiliation Cloud earnings with local commission data
            </p>
          </div>
          {isLoggedIn && (
            <div className="flex items-center gap-3 shrink-0">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <Cloud className="h-4 w-4 text-emerald-400" />
                <span className="text-xs text-emerald-400 font-medium">AC Connected</span>
              </div>
              <button
                onClick={handleLogout}
                className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] rounded-lg border border-[hsl(225,15%,18%)] transition-colors"
              >
                Disconnect
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Login Card (if not connected) */}
      {!isLoggedIn && (
        <div className="max-w-lg mx-auto animate-fade-in space-y-4">
          {/* Method 1: Password login */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10">
                <Cloud className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Connect to Affiliation Cloud</h2>
                <p className="text-xs text-zinc-500">Login with your AC credentials to compare data</p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-3 py-2 bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 focus:border-amber-500/50"
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full px-3 py-2 pr-10 bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 focus:border-amber-500/50"
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  />
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button
                onClick={handleLogin}
                disabled={isLoggingIn}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-black font-medium text-sm rounded-lg transition-colors"
              >
                {isLoggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
                {isLoggingIn ? 'Connecting...' : 'Connect & Compare'}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[hsl(225,15%,18%)]" />
            <span className="text-xs text-zinc-500">OR</span>
            <div className="flex-1 h-px bg-[hsl(225,15%,18%)]" />
          </div>

          {/* Method 2: Paste bearer token */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-6">
            <button
              onClick={() => setShowTokenPaste(!showTokenPaste)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                  <Key className="h-5 w-5 text-blue-400" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-white">Paste Bearer Token</h2>
                  <p className="text-xs text-zinc-500">Copy token from AC browser session</p>
                </div>
              </div>
              {showTokenPaste ? <ChevronDown className="h-5 w-5 text-zinc-500" /> : <ChevronRight className="h-5 w-5 text-zinc-500" />}
            </button>

            {showTokenPaste && (
              <div className="mt-4 space-y-3">
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-xs text-blue-400 font-medium mb-2">How to get your token:</p>
                  <ol className="text-xs text-zinc-400 space-y-1 list-decimal list-inside">
                    <li>Open <a href="https://app.affiliationcloud.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">app.affiliationcloud.com</a> and log in</li>
                    <li>Open DevTools (F12) &rarr; Application &rarr; Local Storage</li>
                    <li>Look for a key containing <code className="text-blue-300 bg-blue-500/10 px-1 rounded">access_token</code> or <code className="text-blue-300 bg-blue-500/10 px-1 rounded">token</code></li>
                    <li>Copy the token value and paste below</li>
                  </ol>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-1">Bearer Token</label>
                  <textarea
                    value={pastedToken}
                    onChange={e => setPastedToken(e.target.value)}
                    placeholder="eyJhbGciOiJSUzI1NiIs..."
                    rows={3}
                    className="w-full px-3 py-2 bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white text-xs font-mono placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 resize-none"
                  />
                </div>
                <button
                  onClick={handleTokenPaste}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm rounded-lg transition-colors"
                >
                  <Key className="h-4 w-4" />
                  Use Token & Compare
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main comparison view (when connected) */}
      {isLoggedIn && (
        <div className="space-y-6 animate-fade-in">
          {/* Controls bar */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Period selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">Period:</span>
              <select
                value={period}
                onChange={e => setPeriod(e.target.value)}
                className="px-3 py-1.5 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-500/50"
              >
                <option value="all">{periodLabel('all')}</option>
                <option value={mar2023ToNow}>{periodLabel(mar2023ToNow)}</option>
                <optgroup label="Individual Months">
                  {months.map(m => (
                    <option key={m} value={m}>
                      {periodLabel(m)}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Refresh */}
            <button
              onClick={fetchComparison}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {/* Debug toggle */}
            <button
              onClick={() => setShowDebug(!showDebug)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                showDebug
                  ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                  : 'text-zinc-500 hover:text-zinc-300 bg-[hsl(225,15%,12%)] border-[hsl(225,15%,20%)]'
              }`}
            >
              <Info className="h-3.5 w-3.5" />
              Debug
            </button>

            <div className="flex-1" />

            {/* Filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5 text-zinc-500" />
              {(['all', 'matched', 'ac_only', 'local_only', 'discrepancies'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilterStatus(f)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                    filterStatus === f
                      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                      : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'matched' ? 'Matched' : f === 'ac_only' ? 'AC Only' : f === 'local_only' ? 'Local Only' : 'Discrepancies'}
                </button>
              ))}
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-3 text-zinc-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Fetching and comparing data...</span>
              </div>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
              <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">Comparison Error</p>
                <p className="text-xs text-red-400/70 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {compareData && !isLoading && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <SummaryCard label="AC Accounts" value={compareData.summary.total_ac_accounts} icon={<Cloud className="h-4 w-4" />} color="amber" />
                <SummaryCard label="Local Accounts" value={compareData.summary.total_local_accounts} icon={<Database className="h-4 w-4" />} color="blue" />
                <SummaryCard label="Matched" value={compareData.summary.matched} icon={<CheckCircle2 className="h-4 w-4" />} color="emerald" />
                <SummaryCard label="AC Only" value={compareData.summary.ac_only} icon={<Cloud className="h-4 w-4" />} color="amber" />
                <SummaryCard label="Local Only" value={compareData.summary.local_only} icon={<Database className="h-4 w-4" />} color="blue" />
                <SummaryCard
                  label="Discrepancies"
                  value={compareData.summary.with_discrepancies}
                  icon={<AlertTriangle className="h-4 w-4" />}
                  color={compareData.summary.with_discrepancies > 0 ? 'red' : 'emerald'}
                />
              </div>

              {/* Totals Comparison — earnings only */}
              <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4 sm:p-6">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <GitCompareArrows className="h-4 w-4 text-amber-400" />
                  Earnings Comparison
                </h3>
                <div className="max-w-md">
                  <TotalCompareCard label="Commission" ac={compareData.totals.ac.total_commission} local={compareData.totals.local.commissions_amount} diff={compareData.totals.diff.commission} isAmount={true} />
                </div>
              </div>

              {/* AC Accounts List */}
              <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
                <div className="p-4 border-b border-[hsl(225,15%,18%)] flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Cloud className="h-4 w-4 text-amber-400" />
                    AC Accounts ({compareData.ac_accounts.length})
                  </h3>
                </div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[hsl(225,15%,12%)]">
                      <tr>
                        <th className="text-left px-3 py-2 text-zinc-500 font-medium">ID</th>
                        <th className="text-left px-3 py-2 text-zinc-500 font-medium">Name</th>
                        <th className="text-left px-3 py-2 text-zinc-500 font-medium">Advertiser</th>
                        <th className="text-left px-3 py-2 text-zinc-500 font-medium">System</th>
                        <th className="text-left px-3 py-2 text-zinc-500 font-medium">Status</th>
                        <th className="text-left px-3 py-2 text-zinc-500 font-medium">Brands</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[hsl(225,15%,15%)]">
                      {compareData.ac_accounts.map(acc => (
                        <tr key={acc.id} className="hover:bg-[hsl(225,15%,12%)]">
                          <td className="px-3 py-2 text-zinc-400 tabular-nums">{acc.id}</td>
                          <td className="px-3 py-2 text-white font-medium">{acc.name}</td>
                          <td className="px-3 py-2 text-zinc-400">{acc.advertiser_name}</td>
                          <td className="px-3 py-2 text-zinc-400">{acc.affiliate_system_name}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              acc.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-500/10 text-zinc-400'
                            }`}>
                              {acc.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-zinc-500">{acc.brands.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Per-Account Comparison Table */}
              {filteredRows.length > 0 && (
                <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
                  <div className="p-4 border-b border-[hsl(225,15%,18%)]">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <GitCompareArrows className="h-4 w-4 text-amber-400" />
                      Per-Account Comparison ({filteredRows.length} rows)
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[hsl(225,15%,12%)]">
                        <tr>
                          <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Account</th>
                          <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Platform / Partner</th>
                          <th className="text-center px-3 py-2.5 text-zinc-500 font-medium">Status</th>
                          <th className="text-center px-3 py-2.5 text-zinc-500 font-medium">Sync</th>
                          <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">AC Earnings</th>
                          <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Local Earnings</th>
                          <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Diff</th>
                          <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Diff %</th>
                          <th className="text-center px-3 py-2.5 text-zinc-500 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(225,15%,15%)]">
                        {filteredRows.map(row => {
                          const hasIssues = row.discrepancies.length > 0;
                          const acComm = row.ac?.total_commission ?? 0;
                          const localComm = row.local?.commissions_amount ?? 0;
                          const diff = acComm - localComm;
                          const pctBase = Math.max(Math.abs(acComm), Math.abs(localComm), 1);
                          const diffPct = (diff / pctBase) * 100;
                          const showDiff = row.match_status === 'matched' && Math.abs(diff) > 0.01;
                          const diffColor = !showDiff ? 'text-zinc-600' : diff > 0 ? 'text-emerald-400' : 'text-red-400';
                          const isResyncing = row.local_account_id ? resyncingAccounts.has(row.local_account_id) : false;
                          const canResync = !!row.local_account_id && hasIssues && !isResyncing;
                          return (
                            <tr
                              key={row.name}
                              className={`hover:bg-[hsl(225,15%,12%)] ${hasIssues ? 'bg-red-500/5' : ''}`}
                            >
                              <td className="px-3 py-2.5">
                                <span className="text-white font-medium">{row.name}</span>
                                {row.ac_id && <span className="text-zinc-600 ml-1.5">#{row.ac_id}</span>}
                              </td>
                              <td className="px-3 py-2.5">
                                {row.platform_provider ? (
                                  <span className="text-blue-400 font-medium">{row.platform_provider}</span>
                                ) : null}
                                {row.platform_name && row.platform_name !== row.platform_provider ? (
                                  <span className={`text-zinc-400 ${row.platform_provider ? ' ml-1.5 before:content-["·"] before:mr-1.5 before:text-zinc-600' : ''}`}>
                                    {row.platform_name}
                                  </span>
                                ) : !row.platform_provider ? (
                                  <span className="text-zinc-600">—</span>
                                ) : null}
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <MatchBadge status={row.match_status} />
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <SyncBadge status={row.last_sync_status} lastSyncAt={row.last_sync_at} />
                              </td>
                              <td className="px-3 py-2.5 text-right text-amber-400 tabular-nums font-medium">
                                {row.ac ? fmtCurrency(acComm) : '—'}
                              </td>
                              <td className="px-3 py-2.5 text-right text-blue-400 tabular-nums font-medium">
                                {row.local ? fmtCurrency(localComm) : '—'}
                              </td>
                              <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${diffColor}`}>
                                {row.match_status === 'matched' ? fmtCurrency(diff) : '—'}
                              </td>
                              <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${diffColor}`}>
                                {showDiff ? fmtPct(diffPct) : row.match_status === 'matched' ? '0%' : '—'}
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                {(canResync || isResyncing) && (
                                  <button
                                    onClick={() => handleForceResync(row.local_account_id!, row.name)}
                                    disabled={isResyncing}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Re-fetch ALL historical data including commissions"
                                  >
                                    <RotateCcw className={`h-3 w-3 ${isResyncing ? 'animate-spin' : ''}`} />
                                    {isResyncing ? 'Syncing...' : 'Resync'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Debug Panel */}
              {showDebug && (
                <div className="bg-[hsl(225,15%,8%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
                  <h3 className="text-sm font-semibold text-zinc-400 mb-3">Debug Info</h3>
                  <div className="space-y-2 text-xs text-zinc-500 font-mono">
                    <p>AC Report Endpoint: <span className="text-zinc-300">{compareData.debug.ac_report_endpoint}</span></p>
                    <p>AC Report Items: <span className="text-zinc-300">{compareData.debug.ac_report_items_count}</span></p>
                    <p>Period: <span className="text-zinc-300">{compareData.period}</span></p>
                    <p>Publisher ID: <span className="text-zinc-300">{compareData.publisher_id}</span></p>
                    <p>Date Range: <span className="text-zinc-300">{compareData.date_range.from} → {compareData.date_range.to}</span></p>
                    {compareData.summary?.match_strategies && (
                      <p>Match Stats: <span className="text-zinc-300">{JSON.stringify(compareData.summary.match_strategies)}</span></p>
                    )}
                    {compareData.debug.ac_report_raw_preview && (
                      <details>
                        <summary className="cursor-pointer text-zinc-400 hover:text-zinc-300">Raw API Response Preview</summary>
                        <pre className="mt-2 p-3 bg-[hsl(225,15%,6%)] rounded-lg overflow-x-auto text-[10px] text-zinc-500">
                          {compareData.debug.ac_report_raw_preview}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SummaryCard: React.FC<{ label: string; value: number; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => {
  const colorMap: Record<string, string> = {
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
  };
  const c = colorMap[color] || colorMap.amber;
  return (
    <div className={`rounded-xl border p-3 ${c.split(' ').slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={c.split(' ')[0]}>{icon}</span>
        <span className="text-xs text-zinc-400">{label}</span>
      </div>
      <p className={`text-xl font-bold tabular-nums ${c.split(' ')[0]}`}>{value}</p>
    </div>
  );
};

const TotalCompareCard: React.FC<{ label: string; ac: number; local: number; diff: number; isAmount: boolean }> = ({ label, ac, local, diff, isAmount }) => {
  const fmt = isAmount ? fmtCurrency : fmtNum;
  const DiffIcon = diff > 0 ? ArrowUpRight : diff < 0 ? ArrowDownRight : Minus;
  const diffColor = diff === 0 ? 'text-zinc-500' : Math.abs(diff) < 0.01 ? 'text-zinc-500' : 'text-red-400';
  return (
    <div className="bg-[hsl(225,15%,12%)] rounded-lg p-3">
      <p className="text-xs text-zinc-500 mb-2">{label}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-amber-400 flex items-center gap-1"><Cloud className="h-3 w-3" /> AC</span>
          <span className="text-sm text-white tabular-nums font-medium">{fmt(ac)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-blue-400 flex items-center gap-1"><Database className="h-3 w-3" /> Local</span>
          <span className="text-sm text-white tabular-nums font-medium">{fmt(local)}</span>
        </div>
        <div className="border-t border-[hsl(225,15%,18%)] pt-1.5 flex items-center justify-between">
          <span className="text-[10px] text-zinc-500">Diff</span>
          <span className={`text-xs font-medium tabular-nums flex items-center gap-0.5 ${diffColor}`}>
            <DiffIcon className="h-3 w-3" />
            {isAmount ? fmtCurrency(Math.abs(diff)) : fmtNum(Math.abs(diff))}
          </span>
        </div>
      </div>
    </div>
  );
};

const MatchBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { label: string; cls: string }> = {
    matched: { label: 'Matched', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    ac_only: { label: 'AC Only', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    local_only: { label: 'Local Only', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  };
  const c = config[status] || config.matched;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
};

const SyncBadge: React.FC<{ status?: string; lastSyncAt?: string | null }> = ({ status, lastSyncAt }) => {
  if (!status || status === 'never') {
    return <span className="inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium bg-zinc-500/10 text-zinc-500 border-zinc-500/20">Never</span>;
  }

  const config: Record<string, { cls: string; label: string }> = {
    success: { cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: 'Synced' },
    partial: { cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', label: 'Partial' },
    running: { cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20', label: 'Running' },
    error: { cls: 'bg-red-500/10 text-red-400 border-red-500/20', label: 'Error' },
    idle: { cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', label: 'Idle' },
  };
  const c = config[status] || config.idle;

  // Format relative time
  let timeAgo = '';
  if (lastSyncAt) {
    try {
      const diff = Date.now() - new Date(lastSyncAt).getTime();
      const mins = Math.floor(diff / 60000);
      const hours = Math.floor(mins / 60);
      const days = Math.floor(hours / 24);
      if (days > 0) timeAgo = `${days}d ago`;
      else if (hours > 0) timeAgo = `${hours}h ago`;
      else if (mins > 0) timeAgo = `${mins}m ago`;
      else timeAgo = 'just now';
    } catch { /* ignore */ }
  }

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${c.cls}`} title={lastSyncAt ? `Last sync: ${lastSyncAt}` : ''}>
      {c.label}
      {timeAgo && <span className="opacity-60">({timeAgo})</span>}
    </span>
  );
};

export default ACCompare;
